import { http } from '@/apis/aipass'
import { env } from '@/libs/env'
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AssignSpaceInput } from '../space/space-reservation-slice'
import dayjs from 'dayjs'
import { timeToMinutes } from '../space/spaces-slice'
import { Reservation } from './search-reservations-slice'
import { TranslateObject } from '@/libs/translate-text'

export const fetchTimeSelections = createAsyncThunk(
  'checkIn/timeSelections',
  async (arg: { hotelId: string; reservationsIds: string[] }, { rejectWithValue }) => {
    try {
      const response = await http({
        method: 'get',
        url: `${env('BASE_API_V3')}/custom-checkin/custom-checkin-time-selection`,
        params: { hotelId: arg.hotelId, reservationIds: arg.reservationsIds },
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const setSelectedTimes = createAsyncThunk(
  'checkIn/updateSelectedTimes',
  async (
    payload: {
      timeSelections: SelectableTimeSelection[]
      times: SelectedTime
      selectedReservations: Reservation[]
    },
    { rejectWithValue },
  ) => {
    const assignSpaces: Array<AssignTimeSelectionInput> = []
    Object.keys(payload.times).forEach(selectionId => {
      const selectedTimeSelection = payload.timeSelections.find(({ selection }) => selection.id === selectionId)
      if (!selectedTimeSelection) {
        return
      }

      const { selection, reservationsByTime } = selectedTimeSelection
      Object.keys(payload.times[selectionId]).forEach(date => {
        if (!payload.times[selectionId][date]) {
          return
        }
        reservationsByTime[date].forEach(reserveId => {
          const reserve = payload.selectedReservations.find(r => r.reservationId === reserveId)
          if (!reserve) {
            return
          }
          const usageTime = payload.times[selectionId][date]
          const usageFrom = dayjs(usageTime)
          const usageTo = usageFrom.add(timeToMinutes(selection.minUsageTime), 'minute')
          const spaceAssign = {
            pax: reserve.paxTotal,
            reservationUserName: reserve.guestName || '',
            timeSelectionId: selectionId,
            reservationId: reserveId,
            spaceId: selection.spaceId,
            usageFrom: usageFrom.format('YYYY-MM-DD HH:mm:ss'),
            usageTo: usageTo.format('YYYY-MM-DD HH:mm:ss'),
            answer: '',
            price: selection.usagePrice,
            tax: selection.tax,
            subjectName: selection.subjectName || selection.title,
          }
          if (selection.stockPerReservation === 'numOfPeople') {
            for (let i = 0; i < reserve.paxTotal; i++) {
              assignSpaces.push(spaceAssign)
            }
          } else if (selection.stockPerReservation === 'numOfRoom' && reserve.reservationRoom?.length) {
            reserve.reservationRoom.forEach(room => {
              assignSpaces.push(spaceAssign)
            })
          } else {
            assignSpaces.push(spaceAssign)
          }
        })
      })
    })
    return { selectedTimes: payload.times, assignSpaces }
  },
)

export const calcUsageNumTotal = (
  timeSelection: SelectableTimeSelection,
  selectedReservations: Reservation[] | undefined,
  date: string,
): number => {
  return timeSelection.reservationsByTime[date].reduce((resp, reserveId) => {
    if (timeSelection.selection.stockPerReservation === 'numOfPeople') {
      return (resp += selectedReservations?.find(r => r.reservationId === reserveId)?.paxTotal || 1)
    } else if (timeSelection.selection.stockPerReservation === 'numOfRoom') {
      return (resp += selectedReservations?.find(r => r.reservationId === reserveId)?.reservationRoom?.length || 1)
    } else {
      return (resp += 1)
    }
  }, 0)
}

export type SelectableTimeSelection = {
  selection: TimeSelection
  mergeAvailabilityTime: AvailabilityTimes
  reservationsByTime: { [date: string]: string[] }
}

type AvailabilityTimes = {
  [date: string]: {
    [time: string]: number
  }
}
export type TimeSelection = {
  id: string
  title: TranslateObject
  description: TranslateObject
  isRequired: boolean
  checkinDate: string
  checkoutDate: string
  spaceId: string
  minUsageTime: string | undefined
  usageInterval: number
  stockPerReservation: 'single' | 'numOfPeople' | 'numOfRoom'
  dayOfApplicable: 'firstNight' | 'lastDay' | 'everyDay'
  dayOfUse: 'appointedDay' | 'nextDay'
  salesHourEnd: string
  usagePrice: number
  tax: number
  subjectName: TranslateObject | undefined
}
export type SelectedTime = {
  [timeSelectionId: string]: {
    [date: string]: string
  }
}

export interface AssignTimeSelectionInput extends AssignSpaceInput {
  reservationId: string
  price: number
  tax: number
  subjectName: TranslateObject
}

const storageKey = 'timeSelectionCheckInData'
const restoreSessionCustomCheckInData = (): SelectedTime => {
  const storage = sessionStorage.getItem(storageKey)
  return storage ? JSON.parse(storage) : {}
}

const restoreAssignSpaces = (): Array<AssignTimeSelectionInput> => {
  const storage = sessionStorage.getItem(`${storageKey}.assignSpaces`)
  return storage ? JSON.parse(storage) : []
}

const timeSelectionsSlice = createSlice({
  name: 'fetchTimeSelections',
  initialState: {
    timeSelections: [] as SelectableTimeSelection[],
    selectedTimes: restoreSessionCustomCheckInData(),
    assignSpaces: restoreAssignSpaces(),
    loading: false,
  },
  reducers: {
    clearTimeSelection: state => {
      state.selectedTimes = {}
      sessionStorage.removeItem(storageKey)
      state.assignSpaces = []
      sessionStorage.removeItem(`${storageKey}.assignSpaces`)
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchTimeSelections.fulfilled, (state, action: PayloadAction<SelectableTimeSelection[]>) => {
      state.timeSelections = action.payload
      state.loading = false
    })
    builder.addCase(fetchTimeSelections.rejected, state => {
      state.loading = false
    })
    builder.addCase(fetchTimeSelections.pending, state => {
      state.loading = true
    })
    builder.addCase(
      setSelectedTimes.fulfilled,
      (
        state,
        action: PayloadAction<{
          selectedTimes: SelectedTime
          assignSpaces: AssignTimeSelectionInput[]
        }>,
      ) => {
        state.selectedTimes = action.payload.selectedTimes
        sessionStorage.setItem(storageKey, JSON.stringify(state.selectedTimes))
        state.assignSpaces = action.payload.assignSpaces
        sessionStorage.setItem(`${storageKey}.assignSpaces`, JSON.stringify(state.assignSpaces))
      },
    )
  },
})

export const { clearTimeSelection } = timeSelectionsSlice.actions
export const timeSelectionsReducer = timeSelectionsSlice.reducer
